import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  TextField,
  Stack
} from '@mui/material';
import {
  Download,
  Battery6Bar,
  HomeWork,
  BuildCircle,
  VpnKey,
  History,
  Login,
  Logout,
  PhoneAndroid,
  PlayArrow,
  Search
} from '@mui/icons-material';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dataverseService } from '../services/dataverseService';
import { handleDataverseLoadError, getDataverseErrorMessage } from '../lib/dataverseErrorHandler';
import { loadDataverseWithDemoFallback } from '../lib/loadDataverseData';
import { orderService } from '../services/orderService';
import { isOrdersEnabled } from '../config/features';
import { listAllKeys, listKeysByLock } from '../services/unlocKeyService';
import { formatKeyTime, mapUnlocAccessRows } from '../lib/phoneKeys';
import {
  KEY_REQUEST_ACTION,
  KEY_REQUEST_ACTION_LABELS,
  KEY_REQUEST_SCOPE,
  KEY_REQUEST_SCOPE_LABELS,
  normalizeMobileNumber
} from '../config/keyRequest';
import LogsPage from './LogsPage';
import { downloadWorkbook } from '../lib/exportExcel';

const BATTERY_STATUS = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  UNKNOWN: 'Unknown'
};

const BATTERY_LABELS = {
  [BATTERY_STATUS.LOW]: 'Lav',
  [BATTERY_STATUS.MEDIUM]: 'Medium',
  [BATTERY_STATUS.HIGH]: 'Høy',
  [BATTERY_STATUS.UNKNOWN]: 'Ukjent'
};

/** Lav først — typisk batteribytte-kø */
const BATTERY_SORT_RANK = {
  [BATTERY_STATUS.LOW]: 0,
  [BATTERY_STATUS.MEDIUM]: 1,
  [BATTERY_STATUS.UNKNOWN]: 2,
  [BATTERY_STATUS.HIGH]: 3
};

function normalizeBatteryStatus(level) {
  if (level === null || level === undefined || level === '' || level === 'NotAvailable') {
    return BATTERY_STATUS.UNKNOWN;
  }
  if (typeof level === 'number') {
    if (level >= 70) return BATTERY_STATUS.HIGH;
    if (level >= 30) return BATTERY_STATUS.MEDIUM;
    return BATTERY_STATUS.LOW;
  }
  const raw = String(level);
  if (/^high$/i.test(raw)) return BATTERY_STATUS.HIGH;
  if (/^medium$/i.test(raw)) return BATTERY_STATUS.MEDIUM;
  if (/^low$/i.test(raw)) return BATTERY_STATUS.LOW;
  return BATTERY_STATUS.UNKNOWN;
}

function batteryChipColor(status) {
  switch (status) {
    case BATTERY_STATUS.HIGH:
      return 'success';
    case BATTERY_STATUS.MEDIUM:
      return 'warning';
    case BATTERY_STATUS.LOW:
      return 'error';
    default:
      return 'default';
  }
}

// Helper functions for care level conversion
function mapManualGrantRows(requests, { locks = [], residents = [], phones = [] } = {}) {
  const phoneMap = new Map();
  for (const phone of phones) {
    const mobile = normalizeMobileNumber(phone.mobileNumber);
    if (mobile) {
      phoneMap.set(mobile, phone);
    }
  }

  return requests.map((request) => {
    const mobile = normalizeMobileNumber(request.mobileNumber) || request.mobileNumber;
    const phone = mobile ? phoneMap.get(mobile) : null;
    const lock = locks.find((entry) => entry.lockId === request.lockId);
    const resident = residents.find((entry) => entry.residentId === request.residentId);

    let target = KEY_REQUEST_SCOPE_LABELS[request.scope] || '—';
    if (request.action === KEY_REQUEST_ACTION.REVOKE && !request.lockId) {
      target = 'Alle nøkler for nummeret';
    } else if (request.scope === KEY_REQUEST_SCOPE.SINGLE_LOCK) {
      target = lock?.name || request.lockId || 'Én lås';
    } else if (request.scope === KEY_REQUEST_SCOPE.ALL_LOCKS_FOR_RESIDENT) {
      target = resident?.name ? `Alle lås for ${resident.name}` : 'Alle lås for innbygger';
    } else if (request.scope === KEY_REQUEST_SCOPE.ALL_LOCKS) {
      target = 'Alle Unloc-låser';
    }

    return {
      id: request.requestId,
      createdOn: request.createdOn,
      createdBy: request.createdBy || 'Ukjent',
      action: request.action,
      actionLabel: KEY_REQUEST_ACTION_LABELS[request.action] || String(request.action),
      recipient: phone?.name || request.mobileNumber || 'Ukjent',
      mobileNumber: request.mobileNumber,
      target,
      lockId: request.lockId,
      durationMinutes: request.durationMinutes,
      statusLabel: request.statusLabel
    };
  });
}

const ReportsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [residents, setResidents] = useState([]);
  const [locks, setLocks] = useState([]);
  const [, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(() =>
    searchParams.get('report') === 'hendelseslogg' ? 'hendelseslogg' : ''
  );
  const [activeReport, setActiveReport] = useState(null);
  const [runId, setRunId] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [phones, setPhones] = useState([]);
  const [accessRows, setAccessRows] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [grantRows, setGrantRows] = useState([]);
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantError, setGrantError] = useState(null);
  const [mobilLogRows, setMobilLogRows] = useState([]);
  const [mobilLogLoading, setMobilLogLoading] = useState(false);
  const [mobilLogError, setMobilLogError] = useState(null);
  const reportIsActive = Boolean(selectedReport) && activeReport === selectedReport;
  const reportRunning =
    reportIsActive && (accessLoading || grantLoading || mobilLogLoading);
  const [batteryStatusFilter, setBatteryStatusFilter] = useState('all');
  const [batterySearch, setBatterySearch] = useState('');
  const [batterySortBy, setBatterySortBy] = useState('batteryRank');
  const [batterySortDir, setBatterySortDir] = useState('asc');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const { data } = await loadDataverseWithDemoFallback(
        () => dataverseService.getAssignmentsData(),
        async () => {
          const { mockApi } = await import('../data/demoData');
          const [residentsData, locksData, assignmentsData] = await Promise.all([
            mockApi.getResidents(),
            mockApi.getLocks(),
            mockApi.getAssignments()
          ]);
          return {
            residents: residentsData,
            locks: locksData,
            assignments: assignmentsData
          };
        }
      );

      setResidents(data.residents);
      setLocks(data.locks);
    } catch (error) {
      console.error('Error loading data:', error);
      if (handleDataverseLoadError(error, navigate)) {
        return;
      }
      setResidents([]);
      setLocks([]);
      setLoadError(getDataverseErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!isOrdersEnabled() && selectedReport === 'serviceMaintenanceLog') {
      setSelectedReport('');
      setActiveReport(null);
    }
  }, [selectedReport]);

  useEffect(() => {
    if (searchParams.get('report') === 'hendelseslogg' && selectedReport !== 'hendelseslogg') {
      setSelectedReport('hendelseslogg');
      setActiveReport(null);
    }
  }, [searchParams, selectedReport]);

  const clearHeavyReportData = () => {
    setAccessRows([]);
    setAccessError(null);
    setAccessLoading(false);
    setGrantRows([]);
    setGrantError(null);
    setGrantLoading(false);
    setMobilLogRows([]);
    setMobilLogError(null);
    setMobilLogLoading(false);
  };

  const handleReportChange = (value) => {
    setSelectedReport(value);
    setActiveReport(null);
    clearHeavyReportData();
    const next = new URLSearchParams(searchParams);
    if (value === 'hendelseslogg') {
      next.set('report', 'hendelseslogg');
    } else {
      ['report', 'lockId', 'phone', 'user', 'source', 'from', 'to', 'accessGroup'].forEach((key) =>
        next.delete(key)
      );
    }
    setSearchParams(next, { replace: true });
  };

  const handleRunReport = () => {
    if (!selectedReport) {
      return;
    }
    setActiveReport(selectedReport);
    setRunId((prev) => prev + 1);
  };

  useEffect(() => {
    if (activeReport !== 'lockAccess' || runId < 1) {
      return undefined;
    }

    let cancelled = false;
    const loadAccess = async () => {
      setAccessLoading(true);
      setAccessError(null);
      try {
        const [listed, phoneData] = await Promise.all([
          listAllKeys().catch(async () => {
            const pages = await Promise.all(
              locks
                .filter((lock) => lock.provider === 'unloc')
                .map((lock) => listKeysByLock(lock.lockId))
            );
            return { keys: pages.flatMap((page) => page?.keys || page?.value || []) };
          }),
          dataverseService.phoneService.getAll().catch(() => [])
        ]);
        if (cancelled || !listed) {
          return;
        }
        setPhones(phoneData);
        setAccessRows(mapUnlocAccessRows(listed, { locks, phones: phoneData }));
      } catch (error) {
        if (!cancelled) {
          setAccessRows([]);
          setAccessError(getDataverseErrorMessage(error, 'Kunne ikke hente tilgang fra Unloc.'));
        }
      } finally {
        if (!cancelled) {
          setAccessLoading(false);
        }
      }
    };

    loadAccess();
    return () => {
      cancelled = true;
    };
  }, [activeReport, runId, locks]);

  useEffect(() => {
    if (activeReport !== 'manualGrants' || runId < 1) {
      return undefined;
    }

    let cancelled = false;
    const loadGrants = async () => {
      setGrantLoading(true);
      setGrantError(null);
      try {
        const [requests, phoneData] = await Promise.all([
          dataverseService.keyRequestService.listManual(),
          dataverseService.phoneService.getAll().catch(() => [])
        ]);
        if (cancelled) {
          return;
        }
        setPhones(phoneData);
        setGrantRows(mapManualGrantRows(requests || [], { locks, residents, phones: phoneData }));
      } catch (error) {
        if (!cancelled) {
          setGrantRows([]);
          setGrantError(getDataverseErrorMessage(error, 'Kunne ikke hente manuelle tildelinger.'));
        }
      } finally {
        if (!cancelled) {
          setGrantLoading(false);
        }
      }
    };

    loadGrants();
    return () => {
      cancelled = true;
    };
  }, [activeReport, runId, locks, residents]);

  useEffect(() => {
    if (activeReport !== 'mobilLog' || runId < 1) {
      return undefined;
    }

    let cancelled = false;
    const loadMobilLogs = async () => {
      setMobilLogLoading(true);
      setMobilLogError(null);
      try {
        // No hard 60-day cap — retain sessions for Unloc correlation beyond Unloc live window.
        const records = await dataverseService.mobilLogService.list({
          top: 5000
        });
        if (cancelled) {
          return;
        }
        const sorted = [...(records || [])].sort(
          (left, right) =>
            new Date(right.timestamp || 0).getTime() - new Date(left.timestamp || 0).getTime()
        );
        setMobilLogRows(sorted);
      } catch (error) {
        if (!cancelled) {
          setMobilLogRows([]);
          setMobilLogError(
            getDataverseErrorMessage(error, 'Kunne ikke hente mobillogg (oc_mobillog).')
          );
        }
      } finally {
        if (!cancelled) {
          setMobilLogLoading(false);
        }
      }
    };

    loadMobilLogs();
    return () => {
      cancelled = true;
    };
  }, [activeReport, runId]);

  const getLockUtilizationData = () => {
    return locks.map(lock => ({
      name: lock.name,
      assigned: lock.assignedResidents.length,
      available: 1 - (lock.assignedResidents.length > 0 ? 1 : 0)
    }));
  };

  const getBatteryStatusRows = useCallback(() => {
    return locks.map((lock) => {
      const batteryStatus = normalizeBatteryStatus(lock.batteryLevel);
      return {
        lockName: lock.name || '',
        lockId: lock.lockId,
        location: lock.location || '',
        batteryStatus,
        batteryLabel: BATTERY_LABELS[batteryStatus] || BATTERY_LABELS[BATTERY_STATUS.UNKNOWN],
        batteryRank: BATTERY_SORT_RANK[batteryStatus] ?? 2,
        lastMaintenance: lock.lastMaintenance || '-'
      };
    });
  }, [locks]);

  const allBatteryRows = useMemo(() => getBatteryStatusRows(), [getBatteryStatusRows]);

  const visibleBatteryRows = useMemo(() => {
    const query = batterySearch.trim().toLowerCase();
    let rows = allBatteryRows;

    if (batteryStatusFilter !== 'all') {
      rows = rows.filter((row) => row.batteryStatus === batteryStatusFilter);
    }
    if (query) {
      rows = rows.filter(
        (row) =>
          row.lockName.toLowerCase().includes(query) ||
          String(row.location || '').toLowerCase().includes(query) ||
          String(row.lockId || '').toLowerCase().includes(query)
      );
    }

    const dir = batterySortDir === 'desc' ? -1 : 1;
    return rows.slice().sort((a, b) => {
      let cmp = 0;
      if (batterySortBy === 'lockName') {
        cmp = a.lockName.localeCompare(b.lockName, 'nb');
      } else if (batterySortBy === 'location') {
        cmp = String(a.location).localeCompare(String(b.location), 'nb');
      } else {
        cmp = a.batteryRank - b.batteryRank;
        if (cmp === 0) {
          cmp = a.lockName.localeCompare(b.lockName, 'nb');
        }
      }
      return cmp * dir;
    });
  }, [allBatteryRows, batteryStatusFilter, batterySearch, batterySortBy, batterySortDir]);

  const handleBatterySort = (column) => {
    if (batterySortBy === column) {
      setBatterySortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setBatterySortBy(column);
    setBatterySortDir(column === 'batteryRank' ? 'asc' : 'asc');
  };

  const getAddressLogRows = () => {
    return residents
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((resident) => ({
        residentName: resident.name,
        residentId: resident.residentId,
        currentAddress: resident.room || 'Ikke oppgitt',
        changedAt: resident.admissionDate || '-',
        changedBy: 'System'
      }));
  };

  const getServiceMaintenanceRows = () => {
    const orderRows = orderService
      .getAll()
      .filter((order) => order.type === 'Service')
      .map((order) => {
        const lockName = locks.find((lock) => lock.lockId === order.lockId)?.name || order.lockId || 'Ukjent lås';
        return {
          date: order.completedAt || order.createdAt,
          lockName,
          activity: order.description || 'Serviceoppdrag',
          result: order.status,
          performedBy: order.assignedTo || order.createdBy || 'System'
        };
      });

    const fallbackRows = locks.map((lock) => ({
      date: lock.lastMaintenance || '-',
      lockName: lock.name,
      activity: 'Periodisk vedlikehold',
      result: 'Utført',
      performedBy: 'Driftsteam'
    }));

    return (orderRows.length > 0 ? orderRows : fallbackRows)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 15);
  };


  const getResidentLockMatrix = () => {
    return residents.map(resident => {
      const assignedLocks = locks.filter(lock => 
        resident.assignedLocks.includes(lock.lockId)
      );
      
      return {
        resident: resident.name,
        residentId: resident.residentId,
        room: resident.room,
        assignedLocks: assignedLocks.map(lock => lock.name).join(', '),
        lockCount: assignedLocks.length
      };
    });
  };

  const formatMobilLogDirection = (direction) => {
    const isLogoff = direction === true || direction === 1 || direction === 'true';
    return isLogoff ? 'Avlogging' : 'Pålogging';
  };

  const handleExport = () => {
    const date = new Date().toISOString().split('T')[0];
    const access = accessRows;

    if (selectedReport === 'batteryStatus') {
      downloadWorkbook(`opencare-batteristatus-${date}.xlsx`, [
        {
          name: 'Batteristatus',
          headers: ['Lås', 'Lås-ID', 'Lokasjon', 'Batteri', 'Sist vedlikehold'],
          rows: visibleBatteryRows.map((row) => ({
            Lås: row.lockName,
            'Lås-ID': row.lockId,
            Lokasjon: row.location,
            Batteri: row.batteryLabel,
            'Sist vedlikehold': row.lastMaintenance
          }))
        }
      ]);
      return;
    }

    if (selectedReport === 'lockUtilization') {
      downloadWorkbook(`opencare-laas-utnyttelse-${date}.xlsx`, [
        {
          name: 'Lås utnyttelse',
          headers: ['Lås', 'Tilordnet', 'Tilgjengelig'],
          rows: getLockUtilizationData().map((row) => ({
            Lås: row.name,
            Tilordnet: row.assigned,
            Tilgjengelig: row.available
          }))
        }
      ]);
      return;
    }

    if (selectedReport === 'residentLockMatrix') {
      downloadWorkbook(`opencare-innbygger-laas-matrise-${date}.xlsx`, [
        {
          name: 'Innbygger-Lås',
          headers: ['Innbygger', 'ID', 'Adresse', 'Låser', 'Antall'],
          rows: getResidentLockMatrix().map((row) => ({
            Innbygger: row.resident,
            ID: row.residentId,
            Adresse: row.room,
            Låser: row.assignedLocks,
            Antall: row.lockCount
          }))
        }
      ]);
      return;
    }

    if (selectedReport === 'addressLog') {
      downloadWorkbook(`opencare-adresselogg-${date}.xlsx`, [
        {
          name: 'Adresselogg',
          headers: ['Innbygger', 'Innbygger-ID', 'Nåværende adresse', 'Sist endret', 'Endret av'],
          rows: getAddressLogRows().map((row) => ({
            Innbygger: row.residentName,
            'Innbygger-ID': row.residentId,
            'Nåværende adresse': row.currentAddress,
            'Sist endret': row.changedAt,
            'Endret av': row.changedBy
          }))
        }
      ]);
      return;
    }

    if (selectedReport === 'lockAccess') {
      downloadWorkbook(`opencare-tilgang-per-laas-${date}.xlsx`, [
        {
          name: 'Tilgang per lås',
          headers: ['Lås', 'Navn', 'Mobil', 'Utstedt', 'Gyldig til'],
          rows: access.map((row) => ({
            Lås: row.lockName,
            Navn: row.holderName,
            Mobil: row.mobileNumber,
            Utstedt: row.grantedOn,
            'Gyldig til': row.expiresOn || 'Varig'
          }))
        }
      ]);
      return;
    }

    if (selectedReport === 'manualGrants') {
      downloadWorkbook(`opencare-manuell-tildeling-${date}.xlsx`, [
        {
          name: 'Manuell tildeling',
          headers: ['Tidspunkt', 'Utført av', 'Handling', 'Mottaker', 'Mobil', 'Mål', 'Status'],
          rows: grantRows.map((row) => ({
            Tidspunkt: row.createdOn,
            'Utført av': row.createdBy,
            Handling: row.actionLabel,
            Mottaker: row.recipient,
            Mobil: row.mobileNumber,
            Mål: row.target,
            Status: row.statusLabel
          }))
        }
      ]);
      return;
    }

    if (selectedReport === 'serviceMaintenanceLog') {
      downloadWorkbook(`opencare-service-vedlikehold-${date}.xlsx`, [
        {
          name: 'Service og vedlikehold',
          headers: ['Dato', 'Lås', 'Gjennomført arbeid', 'Status', 'Utført av'],
          rows: getServiceMaintenanceRows().map((row) => ({
            Dato: row.date,
            Lås: row.lockName,
            'Gjennomført arbeid': row.activity,
            Status: row.result,
            'Utført av': row.performedBy
          }))
        }
      ]);
      return;
    }

    if (selectedReport === 'mobilLog') {
      downloadWorkbook(`opencare-mobillogg-${date}.xlsx`, [
        {
          name: 'Mobillogg',
          headers: ['Tidspunkt', 'Handling', 'Mobil', 'Navn', 'E-post', 'Bruker-ID', 'Enhet'],
          rows: mobilLogRows.map((row) => ({
            Tidspunkt: row.timestamp
              ? new Date(row.timestamp).toLocaleString('nb-NO')
              : '',
            Handling: formatMobilLogDirection(row.direction),
            Mobil: row.mobileNumber,
            Navn: row.name,
            'E-post': row.email,
            'Bruker-ID': row.userId,
            Enhet: row.deviceId
          }))
        }
      ]);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="600">
          Rapporter
        </Typography>
        {reportIsActive && selectedReport !== 'hendelseslogg' && (
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExport}
            sx={{ borderRadius: 2 }}
          >
            Eksporter til Excel
          </Button>
        )}
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Velg og kjør rapport
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Velg rapport, juster filtre om nødvendig, og trykk deretter Kjør rapport.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="reports-filter-report-label">Velg rapport</InputLabel>
                <Select
                  labelId="reports-filter-report-label"
                  id="reports-filter-report"
                  value={selectedReport}
                  label="Velg rapport"
                  onChange={(e) => handleReportChange(e.target.value)}
                >
                  <MenuItem value="lockUtilization">Lås Utnyttelse</MenuItem>
                  <MenuItem value="residentLockMatrix">Innbygger-Lås Matrise</MenuItem>
                  <MenuItem value="batteryStatus">Batteristatus</MenuItem>
                  <MenuItem value="addressLog">Adresselogg</MenuItem>
                  <MenuItem value="lockAccess">Tilgang per lås</MenuItem>
                  <MenuItem value="manualGrants">Manuell tildeling</MenuItem>
                  <MenuItem value="mobilLog">Mobillogg</MenuItem>
                  <MenuItem value="hendelseslogg">Hendelseslogg</MenuItem>
                  {isOrdersEnabled() && (
                    <MenuItem value="serviceMaintenanceLog">Service- og vedlikeholdslogg</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                variant="contained"
                startIcon={reportRunning ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
                onClick={handleRunReport}
                disabled={!selectedReport || reportRunning}
                sx={{ borderRadius: 2 }}
              >
                Kjør rapport
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedReport === 'hendelseslogg' && (
        <LogsPage
          autoLoad={false}
          runId={runId}
          hasRun={activeReport === 'hendelseslogg'}
          hideChrome
        />
      )}

      {!reportIsActive && selectedReport !== 'hendelseslogg' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {selectedReport
            ? 'Ingen rapport kjørt ennå. Trykk Kjør rapport for å vise resultatet.'
            : 'Velg en rapport og trykk Kjør rapport.'}
        </Alert>
      )}

      {reportIsActive && selectedReport === 'lockUtilization' && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Lås Utnyttelse
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsBarChart data={getLockUtilizationData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                  <Bar dataKey="assigned" stackId="a" fill="#0e2841" name="Tilordnet" />
                  <Bar dataKey="available" stackId="a" fill="#e67e22" name="Tilgjengelig" />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {reportIsActive && selectedReport === 'residentLockMatrix' && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Innbygger-Lås Matrise
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Innbygger</strong></TableCell>
                    <TableCell><strong>ID</strong></TableCell>
                    <TableCell><strong>Adresse</strong></TableCell>
                    <TableCell><strong>Antall Låser</strong></TableCell>
                    <TableCell><strong>Tilordnede Låser</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getResidentLockMatrix().map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.resident}</TableCell>
                      <TableCell>{row.residentId}</TableCell>
                      <TableCell>{row.room}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.lockCount}
                          color={row.lockCount > 0 ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.assignedLocks}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {row.assignedLocks || 'Ingen'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {reportIsActive && selectedReport === 'batteryStatus' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Battery6Bar color="success" />
              <Typography variant="h6">Batteristatus</Typography>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ mb: 2 }}
              alignItems={{ sm: 'center' }}
              flexWrap="wrap"
            >
              <TextField
                size="small"
                placeholder="Søk lås eller lokasjon"
                value={batterySearch}
                onChange={(e) => setBatterySearch(e.target.value)}
                InputProps={{
                  startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ minWidth: 220, flex: 1 }}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {[
                  { value: 'all', label: 'Alle' },
                  { value: BATTERY_STATUS.LOW, label: 'Lav' },
                  { value: BATTERY_STATUS.MEDIUM, label: 'Medium' },
                  { value: BATTERY_STATUS.HIGH, label: 'Høy' },
                  { value: BATTERY_STATUS.UNKNOWN, label: 'Ukjent' }
                ].map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    color={batteryStatusFilter === opt.value ? 'primary' : 'default'}
                    variant={batteryStatusFilter === opt.value ? 'filled' : 'outlined'}
                    onClick={() => setBatteryStatusFilter(opt.value)}
                    clickable
                  />
                ))}
              </Stack>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Viser {visibleBatteryRows.length} av {allBatteryRows.length} låser
              {batterySortBy === 'batteryRank' && batterySortDir === 'asc'
                ? ' · sortert med lavt batteri først'
                : ''}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={batterySortBy === 'lockName' ? batterySortDir : false}>
                      <TableSortLabel
                        active={batterySortBy === 'lockName'}
                        direction={batterySortBy === 'lockName' ? batterySortDir : 'asc'}
                        onClick={() => handleBatterySort('lockName')}
                      >
                        Lås
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={batterySortBy === 'location' ? batterySortDir : false}>
                      <TableSortLabel
                        active={batterySortBy === 'location'}
                        direction={batterySortBy === 'location' ? batterySortDir : 'asc'}
                        onClick={() => handleBatterySort('location')}
                      >
                        Lokasjon
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={batterySortBy === 'batteryRank' ? batterySortDir : false}>
                      <TableSortLabel
                        active={batterySortBy === 'batteryRank'}
                        direction={batterySortBy === 'batteryRank' ? batterySortDir : 'asc'}
                        onClick={() => handleBatterySort('batteryRank')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleBatteryRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">
                          Ingen låser matcher filteret. Prøv «Alle» eller fjern søket.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleBatteryRows.map((row) => (
                      <TableRow key={row.lockId}>
                        <TableCell>{row.lockName}</TableCell>
                        <TableCell>{row.location || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.batteryLabel}
                            size="small"
                            color={batteryChipColor(row.batteryStatus)}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {reportIsActive && selectedReport === 'addressLog' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <HomeWork color="primary" />
              <Typography variant="h6">Adresselogg</Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Innbygger</TableCell>
                    <TableCell>Nåværende adresse</TableCell>
                    <TableCell>Sist endret</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getAddressLogRows().map((row) => (
                    <TableRow key={row.residentId}>
                      <TableCell>{row.residentName}</TableCell>
                      <TableCell>{row.currentAddress}</TableCell>
                      <TableCell>{row.changedAt ? new Date(row.changedAt).toLocaleDateString('no-NO') : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {reportIsActive && selectedReport === 'lockAccess' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <VpnKey color="info" />
              <Typography variant="h6">Tilgang per lås</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Gyldige Unloc-nøkler akkurat nå. Hentes live og lagres ikke i OpenCare.
            </Typography>
            {accessError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {accessError}
              </Alert>
            )}
            {accessLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Lås</TableCell>
                      <TableCell>Navn</TableCell>
                      <TableCell>Mobil</TableCell>
                      <TableCell>Utstedt</TableCell>
                      <TableCell>Gyldig til</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accessRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">
                            Ingen gyldige nøkler funnet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      accessRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.lockName}</TableCell>
                          <TableCell>
                            {row.holderName}
                            {row.registered ? (
                              <Chip label="Registrert" size="small" variant="outlined" sx={{ ml: 1 }} />
                            ) : null}
                          </TableCell>
                          <TableCell>{row.mobileNumber || '—'}</TableCell>
                          <TableCell>{formatKeyTime(row.grantedOn)}</TableCell>
                          <TableCell>{row.expiresOn ? formatKeyTime(row.expiresOn) : 'Varig'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {reportIsActive && selectedReport === 'manualGrants' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <History color="primary" />
              <Typography variant="h6">Manuell tildeling</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Hvem som har gitt eller trukket tilbake nøkler fra OpenCare. Loggført i Dataverse.
            </Typography>
            {grantError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {grantError}
              </Alert>
            )}
            {grantLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tidspunkt</TableCell>
                      <TableCell>Utført av</TableCell>
                      <TableCell>Handling</TableCell>
                      <TableCell>Mottaker</TableCell>
                      <TableCell>Mål</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grantRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary">
                            Ingen manuelle tildelinger funnet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      grantRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{formatKeyTime(row.createdOn)}</TableCell>
                          <TableCell>{row.createdBy}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.actionLabel}
                              size="small"
                              color={row.action === KEY_REQUEST_ACTION.REVOKE ? 'warning' : 'primary'}
                            />
                          </TableCell>
                          <TableCell>
                            {row.recipient}
                            {row.mobileNumber ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {row.mobileNumber}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{row.target}</TableCell>
                          <TableCell>{row.statusLabel}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {isOrdersEnabled() && reportIsActive && selectedReport === 'serviceMaintenanceLog' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <BuildCircle color="error" />
              <Typography variant="h6">Service- og vedlikeholdslogg</Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dato</TableCell>
                    <TableCell>Lås</TableCell>
                    <TableCell>Gjennomført arbeid</TableCell>
                    <TableCell>Utført av</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getServiceMaintenanceRows().map((row, index) => (
                    <TableRow key={`${row.lockName}_${index}`}>
                      <TableCell>{row.date ? new Date(row.date).toLocaleDateString('no-NO') : '-'}</TableCell>
                      <TableCell>{row.lockName}</TableCell>
                      <TableCell>{row.activity}</TableCell>
                      <TableCell>{row.performedBy}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {reportIsActive && selectedReport === 'mobilLog' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PhoneAndroid color="primary" />
              <Typography variant="h6">Mobillogg</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              På- og avlogging på telefoner (oc_mobillog). Beholdes for korrelasjon med
              Unloc-historikk utover Unlocs 60-dagers live-vindu.
            </Typography>
            {mobilLogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {mobilLogError}
              </Alert>
            )}
            {mobilLogLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tidspunkt</TableCell>
                      <TableCell>Handling</TableCell>
                      <TableCell>Mobil</TableCell>
                      <TableCell>Navn</TableCell>
                      <TableCell>E-post</TableCell>
                      <TableCell>Bruker-ID</TableCell>
                      <TableCell>Enhet</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mobilLogRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography color="text.secondary">
                            Ingen mobillogg-rader i perioden.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      mobilLogRows.map((row) => {
                        const isLogoff =
                          row.direction === true ||
                          row.direction === 1 ||
                          row.direction === 'true';
                        return (
                          <TableRow key={row.id} hover>
                            <TableCell>
                              {row.timestamp
                                ? new Date(row.timestamp).toLocaleString('nb-NO')
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                icon={isLogoff ? <Logout /> : <Login />}
                                color={isLogoff ? 'default' : 'success'}
                                label={formatMobilLogDirection(row.direction)}
                              />
                            </TableCell>
                            <TableCell>{row.mobileNumber || '—'}</TableCell>
                            <TableCell>{row.name || '—'}</TableCell>
                            <TableCell>{row.email || '—'}</TableCell>
                            <TableCell>{row.userId || '—'}</TableCell>
                            <TableCell>{row.deviceId || '—'}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ReportsPage;
